import debounce from "lodash.debounce";
import { RefreshCcw, Search } from "lucide-react";
import React, {
  ChangeEvent,
  UIEvent,
  useEffect,
  useMemo,
  useState,
} from "react";

import { NetworkStatus } from "@apollo/client/core";
import {
  IgAvatar,
  IgDialog,
  IgTextField,
  IgTooltip,
} from "@intelligems/ig-theme";
import { dotSlice } from "@intelligems/ig-utils";
import {
  CircularProgress,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Stack,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  FetchImagesQueryHookResult,
  Image,
  useFetchImagesQuery,
} from "@src/generated";
import { useDialog } from "@src/hooks/dialog";

import { AddImagesFrontend } from "./AddImagesFrontend";

const Dialog = () => {
  const dialog = useDialog("imageLibrary");
  const [selectedUrl, setSelectedUrl] = useState("");
  const [query, setQuery] = useState("");
  const [images, setImages] = useState([] as Image[]);
  const [loading, setIsLoading] = useState(false);
  const perPage = 50;
  const theme = useTheme();
  const imagesQuery: FetchImagesQueryHookResult = useFetchImagesQuery({
    variables: { first: perPage, query },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (imagesQuery.data) {
      const imagesResult = imagesQuery.data.files.edges
        .map((file) => {
          if (file.node.__typename === "MediaImage") {
            return file.node.image;
          }
        })
        .filter((image) => image) as Image[];
      setImages(imagesResult);
    }
  }, [imagesQuery.data]);

  const urlLabel = (url: string, shorten = true) => {
    const label = url.split("/").pop()?.split("?").shift();
    if (!label) return "Image";
    if (!shorten) return label;
    return dotSlice(label, 18);
  };

  const refresh = async () => {
    void imagesQuery.refetch({ first: perPage });
  };

  const handleSearch = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setQuery(event.target.value);
    void imagesQuery.refetch({ first: perPage });
  };

  const debouncedResults = useMemo(() => {
    return debounce(handleSearch, 500);
  }, []);

  useEffect(() => {
    setIsLoading(imagesQuery.networkStatus !== NetworkStatus.ready);
  }, [imagesQuery.networkStatus]);

  const loadMore = () => {
    // relayStylePagination splices at `args.after` and drops the suffix, so a
    // duplicate in-flight fetchMore with the same cursor can truncate
    // already-loaded pages.
    if (imagesQuery.networkStatus === NetworkStatus.fetchMore) {
      return;
    }
    if (imagesQuery.data?.files.pageInfo.hasNextPage) {
      const cursor = imagesQuery.data?.files.pageInfo.endCursor;
      // The `files` relayStylePagination policy (see gqlProvider.ts) merges and
      // de-duplicates pages in the cache, so no manual `updateQuery` is needed.
      void imagesQuery.fetchMore({
        variables: { first: perPage, query, cursor },
      });
    }
  };

  const handleScroll = (event: UIEvent<HTMLElement>) => {
    if (
      event.currentTarget.scrollHeight -
        event.currentTarget.scrollTop -
        event.currentTarget.clientHeight <
      10
    ) {
      if (imagesQuery.data?.files.pageInfo.hasNextPage) {
        loadMore();
      }
    }
  };

  return (
    <IgDialog
      title={"Shopify image library"}
      showDialog={dialog.isOpen}
      handleClose={dialog.close}
      showCloseIcon={true}
      maxWidth={"lg"}
      dontUseDialogContent={true}
      handleYesClick={() => {
        if (dialog.props?.onUpdateImage) {
          dialog.props.onUpdateImage(selectedUrl);
        }
      }}
      rawContent={
        <Grid
          container
          justifyContent={"center"}
          alignItems={"center"}
          direction={"row"}
          sx={{ height: "500px" }}
        >
          <Grid
            container
            direction={"column"}
            rowSpacing={3}
            sx={{ width: "100%" }}
          >
            <Grid
              container
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              marginBottom={2}
              width={"100%"}
            >
              <Grid size={6}>
                <IgTextField
                  fullWidth
                  onChange={debouncedResults}
                  size={"small"}
                  placeholder={"Search"}
                  variant="outlined"
                  slotProps={{
                    input: {
                      endAdornment: <Search size={16} />,
                    },
                  }}
                />
              </Grid>

              {loading && (
                <CircularProgress sx={{ margin: "auto" }} size={22} />
              )}

              <Stack direction={"row"} spacing={2}>
                {!loading && (
                  <IgTooltip title={"Refresh library"} arrow>
                    <IconButton
                      onClick={() => {
                        void refresh();
                      }}
                      sx={{ color: theme.igIconColors.accent }}
                    >
                      <RefreshCcw color={theme.igIconColors.accent} size={20} />
                    </IconButton>
                  </IgTooltip>
                )}
                <AddImagesFrontend />
              </Stack>
            </Grid>
            {!!images.length && (
              <ImageList
                cols={5}
                sx={{
                  maxHeight: "380px",
                  "&::-webkit-scrollbar": { width: "8px" },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor:
                      theme.igBackgroundColors.interactive.neutral.hover,
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: theme.igPrimitives.gray[900],
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: theme.igPrimitives.gray.A100,
                    },
                  },
                }}
                gap={20}
                onScrollCapture={(e) => handleScroll(e)}
              >
                {images.map((image, i) => (
                  <IgTooltip
                    key={`image-${i}`}
                    title={urlLabel(image.url, false)}
                    arrow
                  >
                    <ImageListItem
                      key={image.url}
                      onClick={() =>
                        image.url !== selectedUrl
                          ? setSelectedUrl(image.url)
                          : setSelectedUrl("")
                      }
                    >
                      <IgAvatar
                        variant="rounded"
                        sx={{
                          border: `2px ${
                            image.url === selectedUrl
                              ? theme.igBorders.accent.default
                              : theme.igBorders.core.default
                          } solid`,
                          borderRadius: "4px",
                          cursor: "pointer",
                          width: "140px",
                          height: "140px",
                          backgroundColor:
                            theme.igBackgroundColors.interactive.neutral
                              .default,
                        }}
                        src={image.url}
                      />
                      <ImageListItemBar
                        position={"below"}
                        actionPosition={"left"}
                        title={urlLabel(image.url)}
                        sx={{
                          "& .MuiImageListItemBar-titleWrap": {
                            paddingBottom: "4px",
                          },
                          "& .MuiImageListItemBar-title": { fontSize: "14px" },
                          textAlign: "left",
                          textOverflow: "ellipsis",
                        }}
                      />
                    </ImageListItem>
                  </IgTooltip>
                ))}
              </ImageList>
            )}
          </Grid>
        </Grid>
      }
      yesButtonText={"Confirm"}
    />
  );
};

interface ImageLibraryDialogProps {
  id: string;
}

export const ImageLibraryDialog = ({ id }: ImageLibraryDialogProps) => {
  const dialog = useDialog("imageLibrary");
  if (dialog?.props?.id !== id) return null;

  return <Dialog />;
};
